/**
 * Wire the Tallivo shadow client at app boot when either
 *   VITE_COMMERCE_INVENTORY_SHADOW_COMPARE=true
 *   VITE_COMMERCE_INVENTORY_SOURCE=tallivo
 * is set. Also reads the tenant + fulfillment scope from `commerce_settings`
 * so nothing is hardcoded.
 *
 * Failures here NEVER break app boot. Supabase remains authoritative until
 * a later stage explicitly moves authority.
 */
import { supabase } from "@/integrations/supabase/client";
import { setTallivoInventoryClient, setShadowDiscrepancySink } from "@/lib/commerce/inventory";
import { createTallivoInventoryClient, persistShadowDiffs } from "@/lib/commerce/tallivoInventoryClient";

type Scope = "primary_location" | "selected_locations" | "any_online_location";

export async function bootstrapTallivoShadow(): Promise<void> {
  try {
    const env = (import.meta as { env?: Record<string, string> }).env ?? {};
    const wantShadow =
      String(env.VITE_COMMERCE_INVENTORY_SHADOW_COMPARE ?? "false").toLowerCase() === "true";
    const wantTallivo =
      String(env.VITE_COMMERCE_INVENTORY_SOURCE ?? "supabase").toLowerCase() === "tallivo";
    if (!wantShadow && !wantTallivo) return;

    const { data } = await supabase
      .from("commerce_settings")
      .select(
        "tallivo_business_id, tallivo_storefront_id, tallivo_online_inventory_scope, tallivo_fulfillment_location_ids",
      )
      .eq("id", true)
      .maybeSingle();

    if (!data?.tallivo_business_id) {
      console.info("[commerce/inventory] shadow requested but no tallivo_business_id configured");
      return;
    }

    const businessId = data.tallivo_business_id;
    const storefrontId = data.tallivo_storefront_id ?? null;
    const scope = (data.tallivo_online_inventory_scope ?? "primary_location") as Scope;
    setTallivoInventoryClient(createTallivoInventoryClient(), {
      businessId,
      onlineInventoryScope: scope,
      fulfillmentLocationIds: data.tallivo_fulfillment_location_ids ?? [],
    });
    setShadowDiscrepancySink((list) =>
      persistShadowDiffs(list, {
        businessId,
        storefrontId,
        locationScope: scope,
      }),
    );
  } catch (err) {
    console.warn("[commerce/inventory] tallivo shadow bootstrap failed", err);
  }
}
